import { useState } from 'react';
import { FormErrorKey } from '../../data/translations';

export type ContactValues = {
  name: string;
  email: string;
  phone: string;
  service: string;
  description: string;
  contactMethod: 'phone' | 'email';
};

export type ContactErrors = Partial<Record<keyof ContactValues, FormErrorKey>>;
type Status = 'idle' | 'submitting' | 'success' | 'error';

const initialValues: ContactValues = {
  name: '',
  email: '',
  phone: '',
  service: 'concrete',
  description: '',
  contactMethod: 'phone',
};

export function useContactForm() {
  const [values, setValues] = useState<ContactValues>(initialValues);
  const [errors, setErrors] = useState<ContactErrors>({});
  const [status, setStatus] = useState<Status>('idle');

  const update = <K extends keyof ContactValues>(key: K, value: ContactValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const submit = async () => {
    const next = validate(values);
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setStatus('submitting');
    try {
      await new Promise((resolve) => setTimeout(resolve, 900));
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const reset = () => {
    setValues(initialValues);
    setErrors({});
    setStatus('idle');
  };

  return { values, errors, status, update, submit, reset };
}

function validate(values: ContactValues): ContactErrors {
  const errors: ContactErrors = {};
  if (!values.name.trim()) errors.name = 'nameRequired';
  if (!values.email.trim()) errors.email = 'emailRequired';
  else if (!/^\S+@\S+\.\S+$/.test(values.email)) errors.email = 'emailInvalid';
  if (!values.phone.trim()) errors.phone = 'phoneRequired';
  else if (values.phone.replace(/\D/g, '').length < 10) errors.phone = 'phoneInvalid';
  return errors;
}
